'use client';

export const CLIENT_BILLING_PAYMENT_SUCCESS_TYPE = 'client-billing-payment-success';
export const CLIENT_BILLING_PAYMENT_SUCCESS_CHANNEL = 'mrw:client-billing-payment-success';
export const CLIENT_BILLING_PAYMENT_SUCCESS_STORAGE_KEY = 'mrw:client-billing-payment-success';
export const CLIENT_BILLING_PAYMENT_FAILURE_TYPE = 'client-billing-payment-failure';
export const CLIENT_BILLING_PAYMENT_FAILURE_CHANNEL = 'mrw:client-billing-payment-failure';
export const CLIENT_BILLING_PAYMENT_FAILURE_STORAGE_KEY = 'mrw:client-billing-payment-failure';

const PAYMENT_NOTIFICATION_CONFIGS = {
    success: {
        type: CLIENT_BILLING_PAYMENT_SUCCESS_TYPE,
        channel: CLIENT_BILLING_PAYMENT_SUCCESS_CHANNEL,
        storageKey: CLIENT_BILLING_PAYMENT_SUCCESS_STORAGE_KEY,
        source: 'robokassa-success-page',
    },
    failure: {
        type: CLIENT_BILLING_PAYMENT_FAILURE_TYPE,
        channel: CLIENT_BILLING_PAYMENT_FAILURE_CHANNEL,
        storageKey: CLIENT_BILLING_PAYMENT_FAILURE_STORAGE_KEY,
        source: 'robokassa-fail-page',
    },
} as const;

export type ClientBillingPaymentSuccessNotification = {
    type: typeof CLIENT_BILLING_PAYMENT_SUCCESS_TYPE;
    id: string;
    clientId: string | null;
    invoiceId: string | null;
    attemptId: string | null;
    occurredAt: string;
    source: 'robokassa-success-page';
};

export type ClientBillingPaymentFailureNotification = {
    type: typeof CLIENT_BILLING_PAYMENT_FAILURE_TYPE;
    id: string;
    clientId: string | null;
    invoiceId: string | null;
    attemptId: string | null;
    occurredAt: string;
    source: 'robokassa-fail-page';
};

type PaymentNotificationKind = keyof typeof PAYMENT_NOTIFICATION_CONFIGS;
type PaymentNotificationByKind = {
    success: ClientBillingPaymentSuccessNotification;
    failure: ClientBillingPaymentFailureNotification;
};
type PaymentNotificationListener<K extends PaymentNotificationKind> = (
    notification: PaymentNotificationByKind[K],
) => void;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const readOptionalString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
};

const createNotificationId = ({
    invoiceId,
    attemptId,
}: {
    invoiceId: string | null;
    attemptId: string | null;
}): string => {
    const suffix =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    return `robokassa:${invoiceId ?? 'unknown'}:${attemptId ?? 'unknown'}:${suffix}`;
};

const tryParseJson = (value: string): unknown => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const normalizeClientBillingPaymentNotification = <K extends PaymentNotificationKind>(
    value: unknown,
    kind: K,
): PaymentNotificationByKind[K] | null => {
    const config = PAYMENT_NOTIFICATION_CONFIGS[kind];
    if (!isRecord(value) || value.type !== config.type) {
        return null;
    }

    const id = readOptionalString(value.id);
    const occurredAt = readOptionalString(value.occurredAt);
    if (!id || !occurredAt || value.source !== config.source) {
        return null;
    }

    return {
        type: config.type,
        id,
        clientId: readOptionalString(value.clientId),
        invoiceId: readOptionalString(value.invoiceId),
        attemptId: readOptionalString(value.attemptId),
        occurredAt,
        source: config.source,
    } as PaymentNotificationByKind[K];
};

export const normalizeClientBillingPaymentSuccessNotification = (
    value: unknown,
): ClientBillingPaymentSuccessNotification | null => normalizeClientBillingPaymentNotification(value, 'success');

export const normalizeClientBillingPaymentFailureNotification = (
    value: unknown,
): ClientBillingPaymentFailureNotification | null => normalizeClientBillingPaymentNotification(value, 'failure');

const createClientBillingPaymentNotificationFromSearchParams = <K extends PaymentNotificationKind>(
    searchParams: URLSearchParams,
    kind: K,
): PaymentNotificationByKind[K] => {
    const config = PAYMENT_NOTIFICATION_CONFIGS[kind];
    const invoiceId = readOptionalString(searchParams.get('InvId'));
    const attemptId = readOptionalString(searchParams.get('Shp_attemptId'));

    return {
        type: config.type,
        id: createNotificationId({ invoiceId, attemptId }),
        clientId: readOptionalString(searchParams.get('Shp_clientId')),
        invoiceId,
        attemptId,
        occurredAt: new Date().toISOString(),
        source: config.source,
    } as PaymentNotificationByKind[K];
};

export const createClientBillingPaymentSuccessNotificationFromSearchParams = (
    searchParams: URLSearchParams,
): ClientBillingPaymentSuccessNotification =>
    createClientBillingPaymentNotificationFromSearchParams(searchParams, 'success');

export const createClientBillingPaymentFailureNotificationFromSearchParams = (
    searchParams: URLSearchParams,
): ClientBillingPaymentFailureNotification =>
    createClientBillingPaymentNotificationFromSearchParams(searchParams, 'failure');

const createBroadcastChannel = (kind: PaymentNotificationKind): BroadcastChannel | null => {
    if (typeof BroadcastChannel === 'undefined') {
        return null;
    }

    try {
        return new BroadcastChannel(PAYMENT_NOTIFICATION_CONFIGS[kind].channel);
    } catch {
        return null;
    }
};

const publishClientBillingPaymentNotification = <K extends PaymentNotificationKind>(
    notification: PaymentNotificationByKind[K],
    kind: K,
): void => {
    const normalizedNotification = normalizeClientBillingPaymentNotification(notification, kind);
    if (!normalizedNotification) {
        return;
    }

    const channel = createBroadcastChannel(kind);
    try {
        channel?.postMessage(normalizedNotification);
    } catch {
        // Best-effort browser notification; localStorage below remains the fallback.
    } finally {
        channel?.close();
    }

    try {
        const storageKey = PAYMENT_NOTIFICATION_CONFIGS[kind].storageKey;
        localStorage.setItem(storageKey, JSON.stringify(normalizedNotification));
        localStorage.removeItem(storageKey);
    } catch {
        // Storage can be unavailable in hardened browser contexts. The payment return page still renders normally.
    }
};

export const publishClientBillingPaymentSuccessNotification = (
    notification: ClientBillingPaymentSuccessNotification,
): void => publishClientBillingPaymentNotification(notification, 'success');

export const publishClientBillingPaymentFailureNotification = (
    notification: ClientBillingPaymentFailureNotification,
): void => publishClientBillingPaymentNotification(notification, 'failure');

const subscribeToClientBillingPaymentNotifications = <K extends PaymentNotificationKind>(
    listener: PaymentNotificationListener<K>,
    kind: K,
): (() => void) => {
    const handleNotification = (value: unknown) => {
        const notification = normalizeClientBillingPaymentNotification(value, kind);
        if (notification) {
            listener(notification);
        }
    };

    const handleStorage = (event: StorageEvent) => {
        if (event.key !== PAYMENT_NOTIFICATION_CONFIGS[kind].storageKey || !event.newValue) {
            return;
        }

        handleNotification(tryParseJson(event.newValue));
    };

    const channel = createBroadcastChannel(kind);
    const handleChannelMessage = (event: MessageEvent) => handleNotification(event.data);

    window.addEventListener('storage', handleStorage);
    channel?.addEventListener('message', handleChannelMessage);

    return () => {
        window.removeEventListener('storage', handleStorage);
        channel?.removeEventListener('message', handleChannelMessage);
        channel?.close();
    };
};

export const subscribeToClientBillingPaymentSuccessNotifications = (
    listener: PaymentNotificationListener<'success'>,
): (() => void) => subscribeToClientBillingPaymentNotifications(listener, 'success');

export const subscribeToClientBillingPaymentFailureNotifications = (
    listener: PaymentNotificationListener<'failure'>,
): (() => void) => subscribeToClientBillingPaymentNotifications(listener, 'failure');
